import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import { MISSIONS_LIST } from '../../utils/constants';

const missionIcons: { [key: string]: React.ReactNode } = {
  developpement: <CodeIcon sx={{ fontSize: 32 }} />,
  bi: <BarChartIcon sx={{ fontSize: 32 }} />,
  cloud: <CloudIcon sx={{ fontSize: 32 }} />,
  cybersecurite: <SecurityIcon sx={{ fontSize: 32 }} />,
  data: <DataUsageIcon sx={{ fontSize: 32 }} />,
  conseil: <BusinessIcon sx={{ fontSize: 32 }} />,
};

const MissionsSection: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      id="missions"
      sx={{
        py: 10,
        backgroundColor: theme.palette.grey[50],
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Nos Réalisations
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            Découvrez quelques-unes de nos missions réussies auprès de nos clients
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {MISSIONS_LIST.map((mission, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={mission.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          p: 1.5,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                          color: 'primary.main',
                          mr: 2,
                        }}
                      >
                        {missionIcons[mission.category]}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="overline"
                          color="primary"
                          sx={{ fontWeight: 600, letterSpacing: 1 }}
                        >
                          {mission.sector}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {mission.title}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.7 }}
                    >
                      {mission.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        Technologies
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        {mission.technologies.map((tech, idx) => (
                          <Chip
                            key={idx}
                            label={tech}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: theme.palette.primary.light,
                              color: theme.palette.primary.main,
                              fontSize: '0.75rem',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    {mission.results && (
                      <Box
                        sx={{
                          mt: 'auto',
                          pt: 2,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'success.main',
                              mr: 1,
                            }}
                          />
                          {mission.results}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default MissionsSection;
